<template>
  <div>
    <!-- page header -->
    <h1 class="page-header">Orders <small>all listings.</small></h1>

    <!-- search -->
    <display-results
      ref="orderSearch"
      v-slot:default="{ items }"
      :index-name="ordersIndex"
      :refinementList="[
        { text: 'Company', value: 'company_name' },
        { text: 'Payment Method', value: 'payment_method' },
        { text: 'Delivery Method', value: 'delivery_method' },
        { text: 'Paid', value: 'paid' },
      ]"
      :facetList="[
        { text: 'Order ID', value: 'id' },
        { text: 'Phone', value: 'phone' },
        { text: 'Voucher Code', value: 'voucher_code' },
      ]"
    >
      <b-table
        ref="orderstable"
        :fields="fields"
        :items="mapItems(items)"
        show-empty
        stacked="sm"
        bordered
        hover
        @row-clicked="row => showDetails(row)"
      >
        <template v-slot:cell(id)="row">
          {{ row.item.id }}
          <b-button variant="primary" size="sm" class="ml-2" title="Copy ID" @click="copyText(row.item.id)">
            <i class="fa fa-copy" />
          </b-button>
        </template>
        <template v-slot:cell(name)="row">
          {{ `${row.item.first_name} ${row.item.last_name}` }}
        </template>

        <template v-slot:cell(delivery_method)="row">
          {{ `${row.item.delivery_method}` }}
          <b-button
            size="sm"
            variant="primary"
            @click="markedPacked(row.item.id)"
            v-if="row.item.delivery_method === 'post' && row.item.delivered_at === null"
            >Mark as Packed</b-button
          >
          <b-button target="_blank" size="sm" class="ml-2" :href="printLocation([row.item])">Print Voucher</b-button>
        </template>

        <template v-slot:cell(paid)="row">
          <b-badge :variant="row.item.paid ? 'primary' : 'warning'">
            {{ row.item.paid ? paidLabel(row.item) : 'Not Paid' }}
          </b-badge>
        </template>

        <template v-slot:cell(full_sum)="row">
          {{ formatMoney(row.item.full_sum) }}
        </template>

        <template v-slot:row-details="{ item }">
          <b-overlay :show="loading === item.id">
            <order-details :order="item" @loaded="loading = 0" @triggerUpdate="triggerSearch" />
          </b-overlay>
        </template>
      </b-table>
    </display-results>
  </div>
</template>

<script>
import OrderDetails from './OrderDetails.vue'
import DisplayResults from '../../components/DisplayResults.vue'
import { formatDate, formatMoney } from '../page-helpers'
import { orders } from '@youwish1/api-package'

export default {
  name: 'OrdersPage',
  components: { DisplayResults, OrderDetails },
  data() {
    return {
      loading: 0,
      current: 0,
      ordersIndex: process.env.VUE_APP_ALGOLIA_ORDERS_INDEX,
    }
  },
  computed: {
    fields() {
      return [
        { key: 'id', label: 'ID', sortable: false },
        { key: 'name', sortable: false },
        { key: 'paid', sortable: false },
        { key: 'full_sum', sortable: false },
        { key: 'delivery_method', sortable: false },
        { key: 'created_at', sortable: false, formatter: formatDate },
      ]
    },
  },
  methods: {
    formatMoney,
    copyText(text) {
      navigator.clipboard.writeText(text).then(() =>
        this.$bvToast.toast(`Order ID has been copied to clipboard.`, {
          title: 'ID copied',
          variant: 'primary',
        })
      )
    },
    mapItems(items) {
      return items.map(item => ({ ...item, _showDetails: this.current === item.id }))
    },
    triggerSearch() {
      this.$refs.orderSearch.triggerSearch()
    },
    async markedPacked(id) {
      await orders.markAsPacked([id])
      this.$refs.orderSearch.triggerSearch()
      this.$bvToast.toast(`Order has been marked as packed.`, {
        title: 'Order marked as packed',
        variant: 'primary',
      })
    },
    printLocation(items) {
      const url = process.env.VUE_APP_API_URL
      const ids = items.map(item => item.id).join(',')
      return `${url}/orders/print?orders=${ids}`
    },
    showDetails(order) {
      this.loading = order.id
      this.current = order.id === this.current ? 0 : order.id
      order._showDetails = !order._showDetails
    },
    paidLabel(order) {
      const tracker = { redeemed: 0, refunded: 0, expired: 0 }
      const count = order.order_details.length
      for (let i = 0; i < count; i++) {
        const { giftcard } = order.order_details[i]
        if (giftcard === null) {
          continue
        }

        // expired
        if (Date.now() - Date.parse(giftcard.valid_to) > 0) {
          tracker.expired++
        }

        // redeemed
        if (giftcard.redeemed_at) {
          tracker.redeemed++
        }

        if (giftcard?.refunded_at) {
          tracker.refunded++
        }
      }

      const labels = []
      const keysCache = Object.keys(tracker)
      const noLabel = keysCache.every(key => tracker[key] === 0)
      const shouldCompound = keysCache.some(key => tracker[key] === count)

      if (!noLabel) {
        if (shouldCompound) {
          const label = keysCache.find(key => tracker[key] === count)
          labels.push(`(${label})`)
        } else {
          keysCache.forEach(key =>
            labels.push(
              `(${tracker[key]}/${count - order.order_details.filter(i => i.type === 'order_addons').length} ${key})`
            )
          )
        }
      }

      return 'Paid' + (noLabel ? '' : ` ${labels.join(' ')}`)
    },
  },
}
</script>
